"use client";
import Script from "next/script";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { Select } from "@repo/ui-parkmobile/Select";
import {
  trackFormSubmit,
  trackFormStart,
  trackFormInputClick,
  trackFormError,
  type FormErrorType,
} from "@/utils/trackingScripts";

/**
 * Stateful wrapper around Select that keeps its own value in React state.
 * This ensures the component re-renders when the user picks a new option,
 * which the previous approach (static value prop from htmlSelect.value) did not.
 */
function HubspotSelectWrapper({
  id,
  placeholder,
  defaultValue,
  options,
  state,
  nativeSelect,
}: {
  id?: string;
  placeholder: string;
  defaultValue: string;
  options: { label: string; value: string }[];
  state: "default" | "error";
  nativeSelect: HTMLSelectElement;
}) {
  const [value, setValue] = useState(defaultValue);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      nativeSelect.value = newValue;
      const event = new Event("change", { bubbles: true });
      nativeSelect.dispatchEvent(event);
    },
    [nativeSelect],
  );

  return (
    <Select
      id={id}
      placeholder={placeholder}
      value={value}
      background="white"
      state={state}
      onValueChange={handleValueChange}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  );
}

export default function HubspotForm({ sectionName }: { sectionName?: string }) {
  const formStartTracked = useRef(false);
  const selectRootsRef = useRef<Map<HTMLSelectElement, ReturnType<typeof createRoot>>>(new Map());

  useEffect(() => {
    const observers: MutationObserver[] = [];
    let visibilityHandler: (() => void) | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const loadHubspot = () => {
      if (cancelled) return;
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || "",
          formId: process.env.NEXT_PUBLIC_HUBSPOT_FORM_ID || "",
          region: process.env.NEXT_PUBLIC_HUBSPOT_REGION || "na1",
          target: "#hubspotForm",
          onFormSubmitted: () => {
            trackFormSubmit({
              section: sectionName || "Hubspot Form",
              formName: sectionName || "Hubspot Form",
              formValue: 1,
              config: "hubspotForm",
              status: "submitted",
            });
          },
          onFormReady: ($form: HTMLElement) => {
            if (cancelled) return;

            const formElement =
              $form instanceof HTMLElement ? $form : ($form as any)[0];

            if (!formElement || !formElement.querySelectorAll) {
              console.error("Invalid form element", $form);
              return;
            }

            const nativeForm = formElement.querySelector("form");
            if (nativeForm) {
              nativeForm.reset();
              nativeForm.setAttribute("autocomplete", "off");
            }

            const style = document.createElement("style");
            style.textContent = `
              #hubspotForm input::placeholder,
              #hubspotForm textarea::placeholder {
                color: var(--typography-colors-text-faded) !important;
                opacity: 1;
              }
              #hubspotForm input::-webkit-input-placeholder,
              #hubspotForm textarea::-webkit-input-placeholder {
                color: var(--typography-colors-text-faded) !important;
                opacity: 1;
              }
              #hubspotForm input::-moz-placeholder,
              #hubspotForm textarea::-moz-placeholder {
                color: var(--typography-colors-text-faded) !important;
                opacity: 1;
              }
              #hubspotForm input:-ms-input-placeholder,
              #hubspotForm textarea:-ms-input-placeholder {
                color: var(--typography-colors-text-faded) !important;
                opacity: 1;
              }
            `;
            document.head.appendChild(style);

            formElement.querySelectorAll("h2").forEach((h2: Element) => {
              (h2 as HTMLElement).style.display = "none";
            });

            formElement
              .querySelectorAll(".hs-richtext")
              .forEach((richtext: Element) => {
                (richtext as HTMLElement).style.display = "none";
              });

            formElement
              .querySelectorAll(".hs_error_rollup")
              .forEach((errorRollup: Element) => {
                (errorRollup as HTMLElement).style.display = "none";
              });

            formElement
              .querySelectorAll(".hs-input")
              .forEach((input: Element) => {
                const htmlInput = input as HTMLElement;

                htmlInput.style.width = "100%";
                htmlInput.style.borderRadius = "12px";
                htmlInput.style.padding = "16px";
                htmlInput.style.minHeight = "56px";
                htmlInput.style.fontSize = "16px";
                htmlInput.style.lineHeight = "26px";
                htmlInput.style.color = "var(--typography-colors-text-body)";
                htmlInput.style.border = "2px solid transparent";
                htmlInput.style.transition = "all 0.2s";
                htmlInput.style.fontFamily = "Inter";
                htmlInput.style.outline = "none";
                htmlInput.style.backgroundColor = "white";

                htmlInput.addEventListener("focus", () => {
                  htmlInput.style.borderColor = "var(--color-color-light)";
                });

                htmlInput.addEventListener("blur", () => {
                  if (!htmlInput.classList.contains("error")) {
                    htmlInput.style.borderColor = "transparent";
                  }
                });
              });

            formElement
              .querySelectorAll("textarea")
              .forEach((textarea: Element) => {
                const htmlTextarea = textarea as HTMLTextAreaElement;

                htmlTextarea.style.width = "100%";
                htmlTextarea.style.borderRadius = "12px";
                htmlTextarea.style.padding = "16px";
                htmlTextarea.style.minHeight = "120px";
                htmlTextarea.style.fontSize = "16px";
                htmlTextarea.style.lineHeight = "26px";
                htmlTextarea.style.color = "var(--typography-colors-text-body)";
                htmlTextarea.style.border = "2px solid transparent";
                htmlTextarea.style.transition = "all 0.2s";
                htmlTextarea.style.fontFamily = "Inter";
                htmlTextarea.style.outline = "none";
                htmlTextarea.style.backgroundColor = "white";
                htmlTextarea.style.resize = "vertical";

                htmlTextarea.addEventListener("focus", () => {
                  htmlTextarea.style.borderColor = "var(--color-color-light)";
                });

                htmlTextarea.addEventListener("blur", () => {
                  if (!htmlTextarea.classList.contains("error")) {
                    htmlTextarea.style.borderColor = "transparent";
                  }
                });
              });

            formElement
              .querySelectorAll("select")
              .forEach((select: Element) => {
                const htmlSelect = select as HTMLSelectElement;

                const options = Array.from(htmlSelect.options)
                  .filter((option) => !option.disabled && option.value)
                  .map((option) => ({
                    label: option.textContent || "",
                    value: option.value,
                  }));

                const placeholder = htmlSelect.options[0]?.disabled
                  ? htmlSelect.options[0].textContent || "Select..."
                  : "Select...";

                htmlSelect.style.display = "none";

                const wrapper = document.createElement("div");
                wrapper.style.width = "100%";
                htmlSelect.parentNode?.insertBefore(wrapper, htmlSelect);

                let selectTracked = false;
                wrapper.addEventListener("click", () => {
                  if (!selectTracked) {
                    const fieldName =
                      htmlSelect.name || htmlSelect.id || "unknown_field";
                    handleFieldFocus(fieldName);
                    selectTracked = true;
                  }
                });

                const renderSelect = (hasError: boolean) => {
                  let root = selectRootsRef.current.get(htmlSelect);
                  if (!root) {
                    root = createRoot(wrapper);
                    selectRootsRef.current.set(htmlSelect, root);
                  }

                  root.render(
                    <HubspotSelectWrapper
                      id={htmlSelect.id}
                      placeholder={placeholder}
                      defaultValue={htmlSelect.value}
                      options={options}
                      state={hasError ? "error" : "default"}
                      nativeSelect={htmlSelect}
                    />,
                  );
                };

                renderSelect(htmlSelect.classList.contains("error"));

                const selectObserver = new MutationObserver((mutations) => {
                  mutations.forEach((mutation) => {
                    if (
                      mutation.type === "attributes" &&
                      mutation.attributeName === "class"
                    ) {
                      renderSelect(htmlSelect.classList.contains("error"));
                    }
                  });
                });
                observers.push(selectObserver);

                selectObserver.observe(htmlSelect, {
                  attributes: true,
                  attributeFilter: ["class"],
                });
              });

            formElement.querySelectorAll("label").forEach((label: Element) => {
              if (!label.classList.contains("hs-error-msg")) {
                (label as HTMLElement).style.display = "none";
              }
            });

            formElement
              .querySelectorAll(".hs-error-msgs")
              .forEach((errorList: Element) => {
                const htmlErrorList = errorList as HTMLElement;
                htmlErrorList.style.marginTop = "8px";
                htmlErrorList.style.padding = "0";
                htmlErrorList.style.listStyle = "none";
              });

            formElement
              .querySelectorAll(".hs-error-msg")
              .forEach((errorMsg: Element) => {
                const htmlErrorMsg = errorMsg as HTMLElement;
                htmlErrorMsg.style.color = "var(--color-color-danger)";
                htmlErrorMsg.style.fontSize = "14px";
                htmlErrorMsg.style.fontFamily = "Inter";
              });

            const inputObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (
                  mutation.type === "attributes" &&
                  mutation.attributeName === "class"
                ) {
                  const target = mutation.target as HTMLElement;
                  if (target.classList.contains("hs-input")) {
                    target.style.width = "100%";
                    target.style.borderRadius = "12px";
                    target.style.padding = "16px";
                    target.style.minHeight = "56px";
                    target.style.fontSize = "16px";
                    target.style.lineHeight = "26px";

                    if (target.classList.contains("error")) {
                      target.style.borderColor = "var(--color-color-danger)";
                    }
                  }
                }
              });
            });
            observers.push(inputObserver);

            formElement
              .querySelectorAll(".hs-input")
              .forEach((input: Element) => {
                inputObserver.observe(input, {
                  attributes: true,
                  attributeFilter: ["class"],
                });
              });

            // Track errors that have already been reported to avoid duplicates
            const trackedErrors = new Set<string>();

            const getFieldName = (
              fieldContainer?: Element | null,
            ): string | undefined => {
              if (!fieldContainer) return undefined;
              const input = fieldContainer.querySelector(
                "input, select, textarea",
              );
              const fieldName = input?.getAttribute("name");
              if (fieldName) return fieldName;
              const label = fieldContainer.querySelector("label");
              return label?.textContent?.trim();
            };

            const detectErrorType = (
              errorMessage: string,
              fieldElement?: Element | null,
            ): FormErrorType => {
              const lowerMessage = errorMessage.toLowerCase();

              if (
                lowerMessage.includes("required") ||
                lowerMessage.includes("enter") ||
                lowerMessage.includes("fill")
              ) {
                return "missing_field";
              }
              if (
                lowerMessage.includes("valid") ||
                lowerMessage.includes("format") ||
                lowerMessage.includes("invalid")
              ) {
                return "validation_error";
              }
              if (
                lowerMessage.includes("captcha") ||
                lowerMessage.includes("robot") ||
                lowerMessage.includes("bot")
              ) {
                return "captcha_error";
              }
              if (
                lowerMessage.includes("checkbox") ||
                lowerMessage.includes("agree") ||
                lowerMessage.includes("terms") ||
                lowerMessage.includes("consent") ||
                fieldElement?.closest(".hs-form-booleancheckbox")
              ) {
                return "checkbox_error";
              }
              return "validation_error";
            };

            const formObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                  if (node.nodeType === 1) {
                    const element = node as Element;

                    if (element.classList.contains("hs_error_rollup")) {
                      (element as HTMLElement).style.display = "none";
                    }

                    if (element.classList.contains("hs-error-msgs")) {
                      (element as HTMLElement).style.marginTop = "8px";
                      (element as HTMLElement).style.padding = "0";
                      (element as HTMLElement).style.listStyle = "none";

                      // Track form errors when error messages appear
                      const errorMsgs =
                        element.querySelectorAll(".hs-error-msg");
                      errorMsgs.forEach((errorMsg: Element) => {
                        const errorText =
                          errorMsg.textContent?.trim() || "Unknown error";
                        const errorKey = `${errorText}`;

                        // Only track each unique error once per form session
                        if (!trackedErrors.has(errorKey)) {
                          trackedErrors.add(errorKey);

                          const fieldContainer =
                            element.closest(".hs-form-field");
                          const errorType = detectErrorType(
                            errorText,
                            fieldContainer,
                          );

                          trackFormError({
                            section: sectionName || "Hubspot Form",
                            config: "hubspotForm",
                            formName: sectionName || "Hubspot Form",
                            errorType,
                            errorMessage: errorText,
                            fieldName: getFieldName(fieldContainer),
                          });
                        }
                      });
                    }

                    // Also check if the element itself is an error message
                    if (element.classList.contains("hs-error-msg")) {
                      const errorText =
                        element.textContent?.trim() || "Unknown error";
                      const errorKey = `${errorText}`;

                      if (!trackedErrors.has(errorKey)) {
                        trackedErrors.add(errorKey);

                        const fieldContainer =
                          element.closest(".hs-form-field");
                        const errorType = detectErrorType(
                          errorText,
                          fieldContainer,
                        );

                        trackFormError({
                          section: sectionName || "Hubspot Form",
                          config: "hubspotForm",
                          formName: sectionName || "Hubspot Form",
                          errorType,
                          errorMessage: errorText,
                          fieldName: getFieldName(fieldContainer),
                        });
                      }
                    }

                    element
                      .querySelectorAll(".hs-error-msg")
                      .forEach((errorMsg: Element) => {
                        const htmlErrorMsg = errorMsg as HTMLElement;
                        htmlErrorMsg.style.color = "var(--color-color-danger)";
                        htmlErrorMsg.style.fontSize = "14px";
                        htmlErrorMsg.style.fontFamily = "Inter";
                      });

                    element
                      .querySelectorAll(".hs_error_rollup")
                      .forEach((errorRollup: Element) => {
                        (errorRollup as HTMLElement).style.display = "none";
                      });
                  }
                });
              });
            });

            observers.push(formObserver);

            formObserver.observe(formElement, {
              childList: true,
              subtree: true,
            });

            formElement
              .querySelectorAll(".hs-form-field")
              .forEach((field: Element) => {
                const htmlField = field as HTMLElement;
                htmlField.style.width = "100%";
                htmlField.style.marginBottom = "var(--spacing-primitives-16)";
              });

            formElement
              .querySelectorAll("fieldset")
              .forEach((fieldset: Element) => {
                const htmlFieldset = fieldset as HTMLElement;
                htmlFieldset.style.display = "flex";
                htmlFieldset.style.flexDirection = "row";
                htmlFieldset.style.flexWrap = "wrap";
                htmlFieldset.style.gap = "var(--spacing-primitives-8)";
                htmlFieldset.style.width = "100%";
                htmlFieldset.style.border = "0";
                htmlFieldset.style.padding = "0";
                htmlFieldset.style.margin = "0";
                htmlFieldset.style.marginBottom =
                  "var(--spacing-primitives-16)";
                htmlFieldset.style.maxWidth = "100%";

                if (fieldset.classList.contains("form-columns-2")) {
                  fieldset
                    .querySelectorAll(".hs-form-field")
                    .forEach((field: Element) => {
                      (field as HTMLElement).style.flex =
                        "1 1 calc((100% - var(--spacing-primitives-16)) / 2)";
                      (field as HTMLElement).style.minWidth = "200px";
                      (field as HTMLElement).style.marginBottom = "0";
                    });
                } else {
                  fieldset
                    .querySelectorAll(".hs-form-field")
                    .forEach((field: Element) => {
                      (field as HTMLElement).style.flex = "1 1 100%";
                      (field as HTMLElement).style.marginBottom = "0";
                    });
                }
              });

            const formTag = formElement.querySelector("form");
            if (formTag) {
              formTag.classList.add(
                "uipm:flex",
                "uipm:flex-col",
                "uipm:gap-[var(--spacing-primitives-16)]",
                "uipm:w-full",
              );
            }

            formElement
              .querySelectorAll("button, input[type='submit']")
              .forEach((btn: Element) => {
                const htmlBtn = btn as HTMLElement;

                htmlBtn.style.display = "inline-flex";
                htmlBtn.style.alignItems = "center";
                htmlBtn.style.justifyContent = "center";
                htmlBtn.style.textAlign = "center";
                htmlBtn.style.transition = "all 0.2s";
                htmlBtn.style.outline = "none";
                htmlBtn.style.cursor = "pointer";
                htmlBtn.style.padding = "16px 20px";
                htmlBtn.style.borderRadius = "var(--button-border-radius)";
                htmlBtn.style.minWidth = "var(--button-min-width)";
                htmlBtn.style.minHeight = "56px";
                htmlBtn.style.height = "auto";
                htmlBtn.style.fontSize = "var(--button-font-size)";
                htmlBtn.style.lineHeight = "1";
                htmlBtn.style.fontFamily = "var(--font-family-sans)";
                htmlBtn.style.fontWeight = "600";
                htmlBtn.style.border = "2px solid transparent";
                htmlBtn.style.textDecoration = "none";
                htmlBtn.style.backgroundColor = "var(--surface-surface-accent)";
                htmlBtn.style.color = "var(--typography-colors-text-inverted)";
                htmlBtn.style.verticalAlign = "middle";

                htmlBtn.addEventListener("mouseenter", () => {
                  htmlBtn.style.backgroundColor =
                    "var(--color-color-accent-hover)";
                });

                htmlBtn.addEventListener("mouseleave", () => {
                  htmlBtn.style.backgroundColor =
                    "var(--surface-surface-accent)";
                });

                htmlBtn.addEventListener("mousedown", () => {
                  htmlBtn.style.backgroundColor =
                    "var(--color-color-accent-focus)";
                });

                htmlBtn.addEventListener("mouseup", () => {
                  htmlBtn.style.backgroundColor =
                    "var(--color-color-accent-hover)";
                });
              });

            // No need to style p elements since richtext is hidden
            // formElement.querySelectorAll("p").forEach((p: Element) => {
            //   p.classList.add(
            //     "simpleRichText",
            //     "uipm:font-body",
            //     "uipm:transition-colors",
            //     "uipm:text-body",
            //     "uipm:tracking-[-0.16px]",
            //     "uipm:xl:tracking-[-0.18px]",
            //     "uipm:font-medium",
            //     "uipm:text-textLight",
            //     "uipm:md:text-textLight",
            //     "uipm:xl:text-textLight",
            //   );
            // });

            let lastTrackedField: string | null = null;
            let lastTrackedTime = 0;
            let ignoreNextFocus = false;
            const DEBOUNCE_MS = 100;

            visibilityHandler = () => {
              if (document.visibilityState === "visible") {
                ignoreNextFocus = true;
                setTimeout(() => {
                  ignoreNextFocus = false;
                }, 100);
              }
            };
            document.addEventListener("visibilitychange", visibilityHandler);

            const handleFieldFocus = (fieldName: string) => {
              if (ignoreNextFocus) {
                return;
              }

              const now = Date.now();

              if (
                fieldName === lastTrackedField &&
                now - lastTrackedTime < DEBOUNCE_MS
              ) {
                return;
              }

              lastTrackedField = fieldName;
              lastTrackedTime = now;

              if (!formStartTracked.current) {
                trackFormStart({
                  section: sectionName || "Hubspot Form",
                  config: "hubspotForm",
                  formName: sectionName || "Hubspot Form",
                });
                formStartTracked.current = true;
              }

              trackFormInputClick({
                section: sectionName || "Hubspot Form",
                config: "hubspotForm",
                formName: sectionName || "Hubspot Form",
                fieldName,
              });
            };

            formElement
              .querySelectorAll("input, select, textarea")
              .forEach((field: Element) => {
                const htmlField = field as
                  | HTMLInputElement
                  | HTMLSelectElement
                  | HTMLTextAreaElement;

                if (htmlField.dataset.trackingAttached === "true") {
                  return;
                }

                if (
                  htmlField.tagName === "SELECT" &&
                  htmlField.style.display === "none"
                ) {
                  return;
                }

                if (htmlField.tagName === "INPUT") {
                  const inputType = (htmlField as HTMLInputElement).type;
                  if (
                    inputType === "submit" ||
                    inputType === "hidden" ||
                    inputType === "button"
                  ) {
                    return;
                  }
                }

                htmlField.addEventListener("focus", (event) => {
                  if (!event.isTrusted) {
                    return;
                  }

                  const fieldName =
                    htmlField.name || htmlField.id || "unknown_field";
                  handleFieldFocus(fieldName);
                });

                htmlField.dataset.trackingAttached = "true";
              });
          },
        });
      } else {
        retryTimer = setTimeout(loadHubspot, 300);
      }
    };

    loadHubspot();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      observers.forEach((obs) => obs.disconnect());
      selectRootsRef.current.forEach((root) => root.unmount());
      selectRootsRef.current.clear();
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
    };
  }, [sectionName]);
  return (
    <div className="uipm:bg-[var(--surface-surface-secondary)] uipm:p-6 uipm:md:p-8 uipm:rounded-[var(--radius-radius-lg)]">
      <Script
        src="//js.hsforms.net/forms/embed/v2.js"
        strategy="afterInteractive"
      />

      <div id="hubspotForm" className="hubspot-form uipm:w-full" />
    </div>
  );
}

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: {
          region: string;
          portalId: string;
          formId: string;
          target: string;
          onFormReady?: (form: HTMLElement) => void;
          onFormSubmitted?: () => void;
        }) => void;
      };
    };
  }
}
